import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch
} from '@smorgrav/velpr-ui'
import { RefreshCw, Save } from 'lucide-react'
import {
  AuthConfigDoc,
  EmailVerificationEnforcement,
  Role
} from '@smorgrav/hundehall_shared'
import { useAuthConfig, saveAuthConfig } from '@/hooks/useAuthConfig'
import { useAuthStats } from '@/hooks/useAuthStats'

export const Route = createFileRoute('/auth')({
  component: AuthConfigPage
})

const ENFORCEMENT_HELP: Record<EmailVerificationEnforcement, string> = {
  [EmailVerificationEnforcement.banner]:
    'UI-nudge kun — ingen handlinger blokkeres.',
  [EmailVerificationEnforcement.softgate]:
    'Blokkerer kun handlingene listet i «Sensitive handlinger».',
  [EmailVerificationEnforcement.hardgate]:
    'Blokkerer hele appen for uverifiserte brukere i scope.'
}

// Parse a comma-separated text field into a trimmed, de-duped string list.
const parseList = (text: string): string[] =>
  Array.from(
    new Set(
      text
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    )
  )

const parseLines = (text: string): string[] =>
  text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)

function AuthConfigPage() {
  const { config, loading } = useAuthConfig()
  const stats = useAuthStats()

  const [draft, setDraft] = useState<AuthConfigDoc>(config)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  // The doc we last took from Firestore, as JSON. Anything else in `draft`
  // means the user has typed since.
  const adoptedRef = useRef<string | null>(null)

  // Adopt the remote doc on first load, and afterwards only while the editor
  // has no unsaved changes. `config` is a live onSnapshot on `config/auth`
  // (useAuthConfig), so it does fire mid-edit — another root admin, or this
  // admin in a second tab, saving the policy. Overwriting `draft` there would
  // silently discard in-progress enforcement/passkey/MFA edits, and since
  // saveAuthConfig writes the whole document the user would then save the
  // other admin's doc back without noticing. Our own save updates `draft`
  // itself, so it does not depend on this effect.
  useEffect(() => {
    const touched =
      adoptedRef.current !== null && adoptedRef.current !== JSON.stringify(draft)
    if (touched) return
    adoptedRef.current = JSON.stringify(config)
    setDraft(config)
  }, [config, draft])

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(config),
    [draft, config]
  )

  // Pull stats once, after the config first loads, scoped to the saved cutoff.
  // (exhaustive-deps is intentionally narrow — refresh is stable and we don't
  // want to re-count on every keystroke; the Oppdater button re-runs on demand.)
  useEffect(() => {
    if (!loading) void stats.refresh(config.emailVerification.cutoffISO)
  }, [loading])

  const ev = draft.emailVerification
  const pk = draft.passkeys
  const mfa = draft.mfa

  const setEV = (patch: Partial<AuthConfigDoc['emailVerification']>) =>
    setDraft((d) => ({
      ...d,
      emailVerification: { ...d.emailVerification, ...patch }
    }))
  const setPK = (patch: Partial<AuthConfigDoc['passkeys']>) =>
    setDraft((d) => ({ ...d, passkeys: { ...d.passkeys, ...patch } }))
  const setMfa = (patch: Partial<AuthConfigDoc['mfa']>) =>
    setDraft((d) => ({ ...d, mfa: { ...d.mfa, ...patch } }))

  const cutoffValid = !ev.cutoffISO || !Number.isNaN(Date.parse(ev.cutoffISO))

  const onSave = async () => {
    if (!cutoffValid) return
    setSaving(true)
    setSaveError(null)
    try {
      // Normalise the cutoff to a canonical ISO instant on the way out.
      const normalized: AuthConfigDoc = {
        ...draft,
        emailVerification: {
          ...ev,
          cutoffISO: ev.cutoffISO ? new Date(ev.cutoffISO).toISOString() : ''
        }
      }
      await saveAuthConfig(normalized)
      // Take the normalised doc as the new baseline ourselves rather than
      // waiting for the snapshot to feed it back — the adopt effect above only
      // runs when the draft is untouched.
      adoptedRef.current = JSON.stringify(normalized)
      setDraft(normalized)
      setSavedAt(Date.now())
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <p className="text-muted-foreground">Laster …</p>
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 pb-24">
      <div>
        <h1 className="text-2xl font-bold">Autentisering</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Plattform-global policy (<code>config/auth</code>) for
          e-postverifisering, passkeys og MFA. Endringer trer i kraft uten
          deploy (server cacher i ~60&nbsp;s).
        </p>
      </div>

      <StatsCard
        stats={stats}
        cutoffISO={ev.cutoffISO}
        cutoffValid={cutoffValid}
      />

      {/* Email verification — the rollout lever */}
      <Card>
        <CardHeader>
          <CardTitle>E-postverifisering</CardTitle>
          <CardDescription>
            Håndhevingsnivå og innfasing av eksisterende brukere via cutoff.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <Field label="Håndheving">
            <Select
              value={ev.enforcement}
              onValueChange={(v) =>
                setEV({ enforcement: v as EmailVerificationEnforcement })
              }
            >
              <SelectTrigger className="w-full max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(EmailVerificationEnforcement).map((e) => (
                  <SelectItem key={e} value={e}>
                    {e}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground mt-1 text-xs">
              {ENFORCEMENT_HELP[ev.enforcement]}
            </p>
          </Field>

          <Field label="Cutoff (ISO 8601, UTC)">
            <div className="flex items-center gap-2">
              <Input
                value={ev.cutoffISO}
                placeholder="2026-06-01T00:00:00.000Z"
                onChange={(e) => setEV({ cutoffISO: e.target.value })}
                className="max-w-xs font-mono"
                aria-invalid={!cutoffValid}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setEV({ cutoffISO: new Date().toISOString() })}
              >
                Nå
              </Button>
              {ev.cutoffISO && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEV({ cutoffISO: '' })}
                >
                  Tøm
                </Button>
              )}
            </div>
            <p className="text-muted-foreground mt-1 text-xs">
              {ev.cutoffISO === ''
                ? 'Tom = ingen brukere i scope ennå.'
                : cutoffValid
                  ? `Konti opprettet etter ${new Date(ev.cutoffISO).toLocaleString()} er i scope.`
                  : 'Ugyldig ISO-instant.'}
            </p>
          </Field>

          <Field label="Sensitive handlinger (komma-separert)">
            <Input
              value={ev.sensitiveActions.join(', ')}
              placeholder="booking, profile"
              onChange={(e) =>
                setEV({ sensitiveActions: parseList(e.target.value) })
              }
              className="max-w-xs"
            />
            <p className="text-muted-foreground mt-1 text-xs">
              Blokkeres under <code>softgate</code>. Kjente nøkler:{' '}
              <code>booking</code>, <code>profile</code>.
            </p>
          </Field>
        </CardContent>
      </Card>

      {/* Passkeys */}
      <Card>
        <CardHeader>
          <CardTitle>Passkeys (WebAuthn)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <ToggleField
            label="Aktivert"
            checked={pk.enabled}
            onChange={(v) => setPK({ enabled: v })}
          />
          <Field label="RP ID">
            <Input
              value={pk.rpId}
              placeholder="velpr.online"
              onChange={(e) => setPK({ rpId: e.target.value })}
              className="max-w-xs"
            />
          </Field>
          <Field label="RP-navn">
            <Input
              value={pk.rpName}
              onChange={(e) => setPK({ rpName: e.target.value })}
              className="max-w-xs"
            />
          </Field>
          <Field label="Tillatte origins (én per linje)">
            <textarea
              value={pk.allowedOrigins.join('\n')}
              onChange={(e) =>
                setPK({ allowedOrigins: parseLines(e.target.value) })
              }
              rows={3}
              className="border-input bg-transparent w-full max-w-md rounded-md border px-3 py-2 font-mono text-sm"
              placeholder={
                'https://velpr.online\nhttps://hundehall-dev.web.app'
              }
            />
          </Field>
        </CardContent>
      </Card>

      {/* MFA */}
      <Card>
        <CardHeader>
          <CardTitle>E-post MFA (OTP)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <ToggleField
            label="Aktivert"
            checked={mfa.enabled}
            onChange={(v) => setMfa({ enabled: v })}
          />
          <Field label="OTP levetid (sekunder)">
            <Input
              type="number"
              value={mfa.otpTtlSeconds}
              onChange={(e) =>
                setMfa({ otpTtlSeconds: Number(e.target.value) })
              }
              className="max-w-[10rem]"
            />
          </Field>
          <Field label="MFA-sesjon levetid (sekunder)">
            <Input
              type="number"
              value={mfa.sessionTtlSeconds}
              onChange={(e) =>
                setMfa({ sessionTtlSeconds: Number(e.target.value) })
              }
              className="max-w-[10rem]"
            />
          </Field>
          <Field label="Påtvungne roller (komma-separert)">
            <Input
              value={mfa.forcedRoles.join(', ')}
              placeholder="owner, admin"
              onChange={(e) =>
                setMfa({ forcedRoles: parseList(e.target.value) as Role[] })
              }
              className="max-w-xs"
            />
            <p className="text-muted-foreground mt-1 text-xs">
              Gyldige roller: {Object.values(Role).join(', ')}.
            </p>
          </Field>
        </CardContent>
      </Card>

      {/* Sticky save bar */}
      <div className="bg-background/95 fixed inset-x-0 bottom-0 border-t p-3 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-6">
          <div className="text-sm">
            {saveError ? (
              <span className="text-destructive">{saveError}</span>
            ) : dirty ? (
              <span className="text-muted-foreground">Ulagrede endringer</span>
            ) : savedAt ? (
              <span className="text-muted-foreground">Lagret ✓</span>
            ) : (
              <span className="text-muted-foreground">Ingen endringer</span>
            )}
          </div>
          <Button onClick={onSave} disabled={!dirty || saving || !cutoffValid}>
            <Save className="mr-1 h-4 w-4" />
            {saving ? 'Lagrer …' : 'Lagre'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function StatsCard({
  stats,
  cutoffISO,
  cutoffValid
}: {
  stats: ReturnType<typeof useAuthStats>
  cutoffISO: string
  cutoffValid: boolean
}) {
  const s = stats.stats
  const verifiedPct =
    s && s.total > 0 ? Math.round((s.verified / s.total) * 100) : 0
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>Brukere</CardTitle>
          <CardDescription>
            Verifiserte vs uverifiserte e-poster, og hvor mange en flipp ville
            påvirket ved gjeldende cutoff.
          </CardDescription>
        </div>
        <Button
          variant="secondary"
          size="sm"
          disabled={stats.loading || !cutoffValid}
          onClick={() => void stats.refresh(cutoffISO)}
        >
          <RefreshCw
            className={`mr-1 h-4 w-4 ${stats.loading ? 'animate-spin' : ''}`}
          />
          Oppdater
        </Button>
      </CardHeader>
      <CardContent>
        {stats.error ? (
          <p className="text-destructive text-sm">{stats.error}</p>
        ) : !s ? (
          <p className="text-muted-foreground text-sm">
            {stats.loading ? 'Teller brukere …' : 'Ingen data ennå.'}
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Totalt" value={s.total} />
            <Stat
              label="Verifisert"
              value={s.verified}
              sub={`${verifiedPct}%`}
            />
            <Stat label="Uverifisert" value={s.unverified} />
            <Stat
              label="I scope ved cutoff"
              value={s.unverifiedInScope}
              hint="Uverifiserte passord-brukere opprettet etter cutoff"
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function Stat({
  label,
  value,
  sub,
  hint
}: {
  label: string
  value: number
  sub?: string
  hint?: string
}) {
  return (
    <div title={hint}>
      <div className="text-2xl font-semibold tabular-nums">
        {value.toLocaleString()}
        {sub && (
          <Badge variant="secondary" className="ml-2 align-middle">
            {sub}
          </Badge>
        )}
      </div>
      <div className="text-muted-foreground text-xs">{label}</div>
    </div>
  )
}

function Field({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">{label}</label>
      {children}
    </div>
  )
}

function ToggleField({
  label,
  checked,
  onChange
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="flex items-center gap-3">
      <Switch checked={checked} onCheckedChange={onChange} />
      <span className="text-sm font-medium">{label}</span>
    </div>
  )
}
